import { useEffect, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

// Minimal full-screen lightbox for viewing product photography at full size.
// Keyboard: Esc to close, ←/→ to navigate.

export const Lightbox = ({ images, initialIndex = 0, onClose, caption }) => {
  const [i, setI] = useState(initialIndex);

  const next = useCallback(
    () => setI((p) => (p + 1) % images.length),
    [images.length],
  );
  const prev = useCallback(
    () => setI((p) => (p - 1 + images.length) % images.length),
    [images.length],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [next, prev, onClose]);

  if (!images?.length) return null;
  const src = images[i];

  return (
    <div
      className="fixed inset-0 z-[200] bg-mb flex flex-col"
      data-testid="lightbox"
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-6 md:px-12 py-5 hairline-b border-subtle">
        <div className="eyebrow-sm text-mb-muted">
          {i + 1} / {images.length} {caption ? `· ${caption}` : ''}
        </div>
        <button
          className="text-mb hover:text-gold transition-colors p-2"
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          data-testid="lightbox-close"
          aria-label="Close"
        >
          <X size={18} strokeWidth={1.25} />
        </button>
      </div>

      <div
        className="flex-1 flex items-center justify-center relative px-6 md:px-20 py-10"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={src}
          alt={caption || 'product'}
          className="max-h-[80vh] max-w-full object-contain hairline border-subtle"
        />

        {images.length > 1 && (
          <>
            <button
              className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 text-mb hover:text-gold transition-colors p-3 hairline border-subtle bg-mb-surface"
              onClick={prev}
              data-testid="lightbox-prev"
              aria-label="Previous"
            >
              <ChevronLeft size={18} strokeWidth={1.25} />
            </button>
            <button
              className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 text-mb hover:text-gold transition-colors p-3 hairline border-subtle bg-mb-surface"
              onClick={next}
              data-testid="lightbox-next"
              aria-label="Next"
            >
              <ChevronRight size={18} strokeWidth={1.25} />
            </button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div
          className="px-6 md:px-12 py-5 hairline-t border-subtle flex items-center gap-3 overflow-x-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((img, idx) => (
            <button
              key={img}
              onClick={() => setI(idx)}
              data-testid={`lightbox-thumb-${idx}`}
              className={`shrink-0 w-14 h-14 overflow-hidden hairline ${
                idx === i ? 'border-gold' : 'border-subtle hover:border-mb-muted'
              }`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Lightbox;
